import { TagWithMetadata } from 'app/api/tag-queries';
import { useEffect, useMemo, useRef, useState } from 'react';
import { i18n } from 'app/utils/i18n';
import { getTagLabel } from 'app/utils/tag-utils';

export type TagFilters = { [group: string]: string[] };

export interface FilterGroup {
  label: string;
  value: string;
  icon?: React.ReactNode;
  options: { label: string; value: string }[];
}

export interface InitialFilter {
  type: string;
  value: string;
  label: string;
}

const filterKeyMap: Record<string, (tag: TagWithMetadata) => string | undefined> = {
  status: (tag) => tag.status,
  validated: (tag) => tag.validationStatus,
  taxonomy: (tag) => tag.taxonomy?.name,
  type: (tag) => tag.type,
  currency: (tag) => tag.unit?.measure ?? undefined,
  context: (tag) => tag.contextId,
};

const SELECTION_SEARCH_TEXT = () => i18n('all.tags.filtered.by.selection');

export const useFilteredTags = (
  tags: TagWithMetadata[],
  filterGroups: FilterGroup[],
  filterTagIds?: string[] | null,
  initialFilter?: InitialFilter | null,
  quickFilter?: ((tag: TagWithMetadata) => boolean) | null,
  lang: string | undefined = 'sv'
) => {
  const [searchInput, setSearchInput] = useState('');
  const [activeFilterIds, setActiveFilterIds] = useState<string[] | null>(filterTagIds?.length ? filterTagIds : null);
  const [filters, setFilters] = useState<TagFilters>(() => {
    if (initialFilter) {
      return { [initialFilter.type]: [initialFilter.value] };
    }
    return {};
  });

  // Sync search text on mount if filter is already active
  useEffect(() => {
    if (activeFilterIds?.length) {
      setSearchInput(SELECTION_SEARCH_TEXT());
    }
  }, [activeFilterIds?.length]);

  // When filterTagIds changes from parent (editor click), activate ID filter with indicator text
  const prevFilterTagIds = useRef(filterTagIds);
  useEffect(() => {
    if (filterTagIds !== prevFilterTagIds.current && filterTagIds && filterTagIds.length > 0) {
      setActiveFilterIds(filterTagIds);
      setSearchInput(SELECTION_SEARCH_TEXT());
    }
    prevFilterTagIds.current = filterTagIds;
  }, [filterTagIds]);

  // Wrap setSearchInput: when user edits/clears the search, clear the ID filter
  const handleSetSearchInput = (value: string) => {
    if (activeFilterIds) {
      setActiveFilterIds(null);
    }
    setSearchInput(value);
  };

  const filteredTags = useMemo(() => {
    const baseTags = activeFilterIds?.length ? tags.filter((tag) => activeFilterIds.includes(tag.id)) : tags;

    const lowerSearch = activeFilterIds ? '' : searchInput.toLowerCase();
    return baseTags.filter((tag) => {
      if (quickFilter && !quickFilter(tag)) {
        return false;
      }

      if (lowerSearch) {
        const label = tag.concept?.labels ? getTagLabel({ languages: tag.concept.labels, currentLang: lang }) || '' : '';
        const conceptName = tag.concept?.name || '';
        if (!label.toLowerCase().includes(lowerSearch) && !conceptName.toLowerCase().includes(lowerSearch)) {
          return false;
        }
      }

      for (const group in filters) {
        const values = filters[group];
        if (!values.length) continue;
        const getValue = filterKeyMap[group];
        if (!getValue) continue;
        const tagValue = getValue(tag);
        if (!tagValue || !values.includes(tagValue)) return false;
      }

      return true;
    });
  }, [tags, searchInput, filters, activeFilterIds, quickFilter, lang]);

  const onAddFilter = (group: string, value: string) => {
    const groupObj = filterGroups.find((g) => g.value === group);
    const optionObj = groupObj?.options.find((opt) => opt.value === value);

    if (!optionObj) return;

    setFilters((prev: { [group: string]: string[] }) => {
      const prevGroup = prev[group] || [];
      if (prevGroup.includes(value)) {
        return { ...prev, [group]: prevGroup.filter((item) => item !== value) };
      } else {
        return { ...prev, [group]: [...prevGroup, value] };
      }
    });
  };

  const onRemoveFilter = (group: string, value: string) => {
    setFilters((prev: { [group: string]: string[] }) => {
      const prevGroup = prev[group] || [];
      const updatedGroup = prevGroup.filter((item) => item !== value);
      if (updatedGroup.length === 0) {
        const { [group]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [group]: updatedGroup };
    });
  };

  return { searchInput, setSearchInput: handleSetSearchInput, filters, filteredTags, onAddFilter, onRemoveFilter };
};
